import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { capturePersonalCacheBoundary } from "@/lib/personal-cache-boundary";
import { format, isPast, parseISO, endOfDay, startOfDay } from "date-fns";
import { parseLocalDate } from "@/lib/dateUtils";
import { useAuth } from "@/hooks/useAuth";
import { useTaskFilters } from "@/hooks/useTaskFilters";
import { useUpdateSharedTask } from "@/lib/queries/community";

// Validated (not merely asserted) shape of the GET /api/shared-tasks response.
// The server returns flat TaskWithOwner rows (task columns plus an `owner`
// object) — see api-server storage getSharedTasks. Only the fields this hook
// consumes are declared; unknown extra fields are stripped by Zod.
import {
  isEditPermissionError,
  isSubtaskPermissionError,
  isRevokedEditAccessError,
  REVOKED_EDIT_ACCESS_MESSAGE,
} from "@/lib/permission-errors";
const SharedTaskRowSchema = z.object({
  id: z.string(),
  userId: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  priority: z.string(),
  completed: z.boolean(),
  dueDate: z.string().nullable(),
  createdAt: z.string().nullable(),
  // Share permission for the current user. Optional for backward compatibility
  // with servers that don't return it yet; missing is treated as view-only
  // (fail-closed for editing).
  permission: z.string().optional(),
  owner: z.object({
    id: z.string(),
    email: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
  }),
});
const SharedTasksResponseSchema = z.array(SharedTaskRowSchema);

// Validated shape of the GET /api/goals response — only the fields the goals
// filter below consumes. Shape drift now fails the query loudly instead of
// silently rendering a wrong (or empty) goals filter.
const GoalRowSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string(),
  progress: z.number(),
  isFutureGoal: z.boolean(),
});
const GoalsResponseSchema = z.array(GoalRowSchema);
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import {
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { sanitizeCategory } from "@/lib/categoryUtils";
import { z } from "zod";
import type {
  Task,
  TaskWithOwner,
  KanbanColumn,
  Goal,
  JournalEntry,
  SubTask,
  FriendWithUser,
  AssistantWithUser,
  Category,
  TaskProgressEntryWithUser,
  TeamMember,
} from "../pages/task-types";

export function useTasksPage() {
  const { filters, updateFilters } = useTaskFilters({
    filterTab: "all",
    completionFilter: "all",
    categoryFilter: [],
    assignedToFilter: "all",
    selectedAssigneeNames: [],
    searchQuery: "",
    showOnlyLatestRecurring: false,
    sortBy1: "displayOrder",
    sortOrder1: "asc",
    sortBy2: "none",
    sortOrder2: "asc",
    sortBy3: "none",
    sortOrder3: "asc",
    groupBy1: "none",
    groupBy2: "none",
    groupBy3: "none",
    viewMode: "list",
  });

  const [activeMainTab, setActiveMainTab] = useState("my-tasks");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createTaskColumnId, setCreateTaskColumnId] = useState<string | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);
  const pendingDeleteRef = useRef<{ taskId: string; timeoutId: ReturnType<typeof setTimeout> } | null>(null);
  const [viewingTask, setViewingTask] = useState<Task | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [createNoteForTask, setCreateNoteForTask] = useState<Task | null>(null);
  const [isColumnDialogOpen, setIsColumnDialogOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState<KanbanColumn | null>(null);
  const [deletingColumnId, setDeletingColumnId] = useState<string | null>(null);
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [isBulkActionsOpen, setIsBulkActionsOpen] = useState(false);
  const [pendingBulkUpdates, setPendingBulkUpdates] = useState<{
    category?: string;
    dueDate?: string;
    priority?: string;
    tags?: string[];
    assignedTo?: "Me" | "Others";
    assignedToOther?: string;
    assignedToUserId?: string;
  }>({});
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharingTask, setSharingTask] = useState<Task | null>(null);
  const [schedulingTask, setSchedulingTask] = useState<Task | null>(null);
  const [isProgressLogOpen, setIsProgressLogOpen] = useState(false);
  const [progressLogCategoryFilter, setProgressLogCategoryFilter] = useState<string>("all");
  const [progressLogUserFilter, setProgressLogUserFilter] = useState<string>("all");
  const [progressLogStartDate, setProgressLogStartDate] = useState<string>("");
  const [progressLogEndDate, setProgressLogEndDate] = useState<string>("");
  const [isQuickProgressOpen, setIsQuickProgressOpen] = useState(false);
  const [quickProgressTaskId, setQuickProgressTaskId] = useState<string>("");
  const [quickProgressNote, setQuickProgressNote] = useState<string>("");
  const [quickProgressPopoverId, setQuickProgressPopoverId] = useState<string | null>(null);
  const [quickProgressPopoverNote, setQuickProgressPopoverNote] = useState<string>("");
  const [perTaskCompletedOverrides, setPerTaskCompletedOverrides] = useState<Set<string>>(new Set());
  const perTaskOverridesInitialized = useRef(false);

  const { user, tenant } = useAuth();
  const { toast } = useToast();

  const getHideCompleted = (taskId: string): boolean => {
    const global = !!filters.hideCompletedSubtasks;
    const overridden = perTaskCompletedOverrides.has(taskId);
    return overridden ? !global : global;
  };

  const togglePerTaskHideCompleted = useCallback((taskId: string) => {
    const next = new Set(perTaskCompletedOverrides);
    if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
    setPerTaskCompletedOverrides(next);
    updateFilters({ perTaskHideCompleted: [...next] });
  }, [perTaskCompletedOverrides, updateFilters]);

  const isFiltersExpanded = filters.filtersExpanded ?? true;
  const setIsFiltersExpanded = (expanded: boolean) => {
    updateFilters({ filtersExpanded: expanded });
  };

  useEffect(() => {
    if (filters.assignedToFilter !== "others") {
      updateFilters({ selectedAssigneeNames: [] });
    }
  }, [filters.assignedToFilter, updateFilters]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const { data: tasks = [], isLoading: tasksLoading, error: tasksError, refetch: refetchTasks } = useQuery<Task[]>({
    queryKey: ["/api/tasks"],
    // Assignments can be created in another person's browser. The global
    // infinite-fresh cache would otherwise keep an old empty list indefinitely.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const {
    data: assignedToMeTasks = [],
    isLoading: assignedToMeLoading,
    error: assignedToMeError,
    refetch: refetchAssignedToMe,
  } = useQuery<TaskWithOwner[]>({
    queryKey: ["/api/tasks/assigned-to-me"],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const { data: bulkFriends = [] } = useQuery<FriendWithUser[]>({
    queryKey: ["/api/friends"],
  });

  const { data: bulkAssistants = [] } = useQuery<AssistantWithUser[]>({
    queryKey: ["/api/assistants"],
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editTaskId = params.get("edit");
    if (editTaskId && tasks.length > 0) {
      const taskToEdit = tasks.find(t => t.id === editTaskId);
      if (taskToEdit) {
        setEditingTask(taskToEdit);
        window.history.replaceState({}, "", "/tasks");
      }
    }
  }, [tasks]);

  const { data: sharedTasks = [], isLoading: sharedTasksLoading } = useQuery({
    queryKey: ["/api/shared-tasks"],
    select: (data: unknown) => SharedTasksResponseSchema.parse(data),
    // Refetch shared tasks whenever the browser tab regains focus so that a
    // revoked share disappears without requiring a manual page reload.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  const updateSharedTaskMutation = useUpdateSharedTask();

  const combinedTasks = useMemo(() => {
    const sharedAsTaskFormat: Task[] = sharedTasks.map((st) => ({
      id: st.id,
      tenantId: "",
      userId: st.userId,
      goalId: null,
      milestoneId: null,
      journalEntryId: null,
      columnId: null,
      title: st.title,
      description: st.description,
      category: "",
      priority: st.priority || "medium",
      tags: [],
      assignedTo: "Me",
      assignedToOther: null,
      assignedToUserId: null,
      completed: st.completed,
      isTopThree: false,
      displayOrder: 9999,
      dueDate: st.dueDate,
      createdAt: st.createdAt ?? new Date().toISOString(),
      isShared: true,
      sharedPermission: st.permission === "edit" ? "edit" as const : "view" as const,
      sharedBy: {
        firstName: st.owner.firstName ?? "",
        lastName: st.owner.lastName ?? "",
        email: st.owner.email ?? "",
      },
      shareId: st.id,
    }));
    const ownTaskIds = new Set(tasks.map(t => t.id));
    const uniqueShared = sharedAsTaskFormat.filter(st => !ownTaskIds.has(st.id));
    return [...tasks, ...uniqueShared];
  }, [tasks, sharedTasks]);

  const { data: allSubTasks = [] } = useQuery<SubTask[]>({
    queryKey: ["/api/subtasks/all"],
  });

  const subTasksByTaskId = useMemo(() => {
    const grouped = new Map<string, SubTask[]>();
    allSubTasks.forEach(subTask => {
      if (!grouped.has(subTask.taskId)) grouped.set(subTask.taskId, []);
      grouped.get(subTask.taskId)!.push(subTask);
    });
    return grouped;
  }, [allSubTasks]);

  const getSubTaskProgress = (taskId: string) => {
    const taskSubTasks = subTasksByTaskId.get(taskId) || [];
    if (taskSubTasks.length === 0) return null;
    const completed = taskSubTasks.filter(st => st.completed).length;
    const total = taskSubTasks.length;
    return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  const subtaskMutationErrorToast = (action: string) => (error: any) => {
    const permissionError = isSubtaskPermissionError(error);
    toast({
      title: permissionError ? "View Only" : "Error",
      description: permissionError
        ? "You can't edit this shared task — you only have view access."
        : `Failed to ${action}. Please try again.`,
      variant: "destructive",
    });
  };

  const inlineToggleSubTaskMutation = useMutation({
    mutationFn: async ({ id, completed }: { id: string; completed: boolean }) => {
      const response = await apiRequest("PATCH", `/api/subtasks/${id}`, { completed });
      return response.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] }); },
    onError: subtaskMutationErrorToast("update the sub-task"),
  });

  const inlineUpdateSubTaskMutation = useMutation({
    mutationFn: async ({ id, dueDate }: { id: string; dueDate: string | null }) => {
      const isoDate = dueDate ? parseLocalDate(dueDate).toISOString() : null;
      const response = await apiRequest("PATCH", `/api/subtasks/${id}`, { dueDate: isoDate });
      return response.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] }); },
    onError: subtaskMutationErrorToast("update the sub-task due date"),
  });

  const inlineReorderSubTasksMutation = useMutation({
    mutationFn: async (updates: Array<{ id: string; displayOrder: number }>) => {
      await apiRequest("PATCH", "/api/subtasks/reorder", { updates });
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] }); },
    onError: (error: any) => {
      // A 403 carrying skippedSubTaskIds means the share was downgraded from
      // edit to view mid-session — tell the user their access changed instead
      // of showing the generic failure copy.
      if (isRevokedEditAccessError(error)) {
        toast({
          title: "Edit access removed",
          description: REVOKED_EDIT_ACCESS_MESSAGE,
          variant: "destructive",
        });
        // Refetch the share/permission data so the UI flips to view-only
        // (hides drag handles / edit controls) without a manual refresh.
        queryClient.invalidateQueries({ queryKey: ["/api/shared-tasks"] });
      } else {
        subtaskMutationErrorToast("reorder the sub-tasks")(error);
      }
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
    },
  });

  const inlineStarSubTaskMutation = useMutation({
    mutationFn: async ({ id, isStarred }: { id: string; isStarred: boolean }) => {
      const response = await apiRequest("PATCH", `/api/subtasks/${id}`, { isStarred });
      return response.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] }); },
    onError: (error: any) => {
      const permissionError = isEditPermissionError(error);
      toast({
        title: permissionError ? "View Only" : "Error",
        description: permissionError
          ? "You can't edit this shared task — you only have view access."
          : "Failed to update the sub-task star. Please try again.",
        variant: "destructive",
      });
    },
  });

  const inlineUpdateSubTaskTextMutation = useMutation({
    mutationFn: async ({ id, text }: { id: string; text: string }) => {
      const response = await apiRequest("PATCH", `/api/subtasks/${id}`, { text });
      return response.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] }); },
    onError: subtaskMutationErrorToast("update the sub-task text"),
  });

  const inlineDeleteSubTaskMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/subtasks/${id}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
    },
    onError: (error: any) => {
      // Restore the sub-task by re-fetching from the server so an optimistic
      // removal (e.g. from a 403 on a view-only share) doesn't cause the item
      // to silently vanish from the tasks list.
      subtaskMutationErrorToast("delete the sub-task")(error);
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
    },
  });

  const onSubtaskToggle = (id: string) => {
    const st = allSubTasks.find(s => s.id === id);
    if (st) inlineToggleSubTaskMutation.mutate({ id, completed: !st.completed });
  };

  const onSubtaskStar = (id: string) => {
    const st = allSubTasks.find(s => s.id === id);
    if (st) inlineStarSubTaskMutation.mutate({ id, isStarred: !st.isStarred });
  };

  const onSubtaskDateChange = (id: string, date: string | null) => {
    inlineUpdateSubTaskMutation.mutate({ id, dueDate: date });
  };

  const onSubtaskReorder = (_taskId: string, reordered: SubTask[]) => {
    inlineReorderSubTasksMutation.mutate(reordered.map((st, i) => ({ id: st.id, displayOrder: i })));
  };

  const onSubtaskTextChange = (id: string, text: string) => {
    inlineUpdateSubTaskTextMutation.mutate({ id, text });
  };

  const onSubtaskDelete = (id: string) => {
    inlineDeleteSubTaskMutation.mutate(id);
  };

  const { data: allGoals = [] } = useQuery({
    queryKey: ["/api/goals"],
    select: (data: unknown) => GoalsResponseSchema.parse(data),
  });
  const goals: Goal[] = allGoals.filter(g =>
    g.status !== "completed" && g.status !== "future" && g.progress < 100 && !g.isFutureGoal
  );

  const { data: journalEntries = [] } = useQuery<JournalEntry[]>({ queryKey: ["/api/journal"] });
  const { data: categories = [] } = useQuery<Category[]>({ queryKey: ["/api/categories?includeGoalsOnly=true"] });

  const { data: progressLogAssignees = [] } = useQuery<{ id: string; name: string; email: string | null; profileImageUrl: string | null }[]>({
    queryKey: ["/api/task-progress/assignees"],
    enabled: isProgressLogOpen,
  });

  const progressLogQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (progressLogCategoryFilter && progressLogCategoryFilter !== "all") params.append("category", progressLogCategoryFilter);
    if (progressLogUserFilter && progressLogUserFilter !== "all") params.append("userId", progressLogUserFilter);
    if (progressLogStartDate) params.append("startDate", progressLogStartDate);
    if (progressLogEndDate) params.append("endDate", progressLogEndDate);
    return params.toString();
  }, [progressLogCategoryFilter, progressLogUserFilter, progressLogStartDate, progressLogEndDate]);

  const { data: progressLogEntries = [], isLoading: progressLogLoading } = useQuery<TaskProgressEntryWithUser[]>({
    queryKey: ["/api/task-progress", progressLogQueryParams],
    queryFn: async () => {
      const url = progressLogQueryParams ? `/api/task-progress?${progressLogQueryParams}` : "/api/task-progress";
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch progress entries");
      return response.json();
    },
    enabled: isProgressLogOpen,
  });

  const { data: columns = [], isLoading: columnsLoading } = useQuery<KanbanColumn[]>({
    queryKey: ["/api/kanban/columns"],
    enabled: filters.viewMode === "kanban",
  });

  const moveTaskMutation = useMutation({
    mutationFn: async ({ taskId, columnId }: { taskId: string; columnId: string | null }) => {
      await apiRequest("PATCH", `/api/tasks/${taskId}/column`, { columnId });
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/tasks"] }); },
    onError: () => {},
  });

  const createColumnMutation = useMutation({
    mutationFn: async (column: { title: string; color: string; tenantId: string; userId: string; displayOrder: number }) => {
      return apiRequest("POST", "/api/kanban/columns", column);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/kanban/columns"] });
      setIsColumnDialogOpen(false);
      setEditingColumn(null);
    },
    onError: () => {},
  });

  const updateColumnMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: { title?: string; color?: string } }) => {
      return apiRequest("PATCH", `/api/kanban/columns/${id}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/kanban/columns"] });
      setIsColumnDialogOpen(false);
      setEditingColumn(null);
    },
    onError: () => {},
  });

  const deleteColumnMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/kanban/columns/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/kanban/columns"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      setDeletingColumnId(null);
    },
    onError: () => {},
  });

  const reorderColumnsMutation = useMutation({
    mutationFn: async (columnIds: string[]) => {
      await apiRequest("POST", "/api/kanban/columns/reorder", { columnIds });
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/kanban/columns"] }); },
    onError: () => {},
  });

  const getGroupValue = (task: Task, groupField: string): string => {
    switch (groupField) {
      case "category":
        return sanitizeCategory(task.category) || "Uncategorized";
      case "priority":
        return task.priority || "medium";
      case "assignedTo": {
        const assignee = task.assignedTo === "Others" && task.assignedToOther
          ? task.assignedToOther : task.assignedTo;
        return (typeof assignee === "string" && assignee) ? assignee : "Unassigned";
      }
      case "status":
        return task.completed ? "Completed" : "Active";
      case "dueDate": {
        if (!task.dueDate) return "No Due Date";
        const dueDate = new Date(task.dueDate);
        if (isPast(dueDate) && !task.completed) return "Overdue";
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);
        if (format(dueDate, "yyyy-MM-dd") === format(today, "yyyy-MM-dd")) return "Today";
        if (format(dueDate, "yyyy-MM-dd") === format(tomorrow, "yyyy-MM-dd")) return "Tomorrow";
        if (dueDate <= nextWeek) return "This Week";
        return "Later";
      }
      default:
        return "None";
    }
  };

  // Shared filter pipeline (everything except the Starred/top-three filter),
  // used by the main task list AND by the flat starred sub-task section so
  // both honor the same tab/search/category/assignee/date/completion filters.
  const preStarFilteredTasks = useMemo(() => {
    let filtered = combinedTasks;

    if (filters.filterTab === "standalone") {
      filtered = filtered.filter(t => !t.goalId && !t.milestoneId && !t.journalEntryId);
    } else if (filters.filterTab === "goals") {
      filtered = filtered.filter(t => t.goalId);
    } else if (filters.filterTab === "milestones") {
      filtered = filtered.filter(t => t.milestoneId);
    } else if (filters.filterTab === "journal") {
      filtered = filtered.filter(t => t.journalEntryId);
    }

    if (filters.completionFilter === "active") {
      filtered = filtered.filter(t => !t.completed);
    } else if (filters.completionFilter === "completed") {
      filtered = filtered.filter(t => t.completed);
    }

    if (filters.categoryFilter && filters.categoryFilter.length > 0) {
      filtered = filtered.filter(t => t.category && filters.categoryFilter!.includes(t.category));
    }

    if (filters.assignedToFilter === "me") {
      filtered = filtered.filter(t =>
        !t.isShared && (t.assignedTo === "Me" || t.assignedToUserId === user?.id)
      );
    } else if (filters.assignedToFilter === "others") {
      filtered = filtered.filter(t => t.assignedTo === "Others");
      if (filters.selectedAssigneeNames && filters.selectedAssigneeNames.length > 0) {
        filtered = filtered.filter(t => t.assignedToOther && filters.selectedAssigneeNames!.includes(t.assignedToOther));
      }
    } else if (filters.assignedToFilter === "sharedWithMe") {
      filtered = filtered.filter(t => t.isShared === true);
    }

    if (filters.searchQuery) {
      filtered = filtered.filter(t =>
        (t.title ?? "").toLowerCase().includes(filters.searchQuery!.toLowerCase()) ||
        t.description?.toLowerCase().includes(filters.searchQuery!.toLowerCase())
      );
    }

    if (filters.dateRangeStart || filters.dateRangeEnd) {
      filtered = filtered.filter(t => {
        if (!t.dueDate) return false;
        const taskDueDate = typeof t.dueDate === "string" ? parseISO(t.dueDate) : new Date(t.dueDate);
        const startDate = filters.dateRangeStart ? startOfDay(new Date(filters.dateRangeStart + "T00:00:00")) : null;
        const endDate = filters.dateRangeEnd ? endOfDay(new Date(filters.dateRangeEnd + "T00:00:00")) : null;
        if (startDate && endDate) return taskDueDate >= startDate && taskDueDate <= endDate;
        if (startDate) return taskDueDate >= startDate;
        if (endDate) return taskDueDate <= endDate;
        return true;
      });
    }

    return filtered;
  }, [combinedTasks, filters.filterTab, filters.completionFilter, filters.categoryFilter, filters.assignedToFilter, filters.selectedAssigneeNames, filters.searchQuery, filters.dateRangeStart, filters.dateRangeEnd, user?.id]);

  // Starred sub-tasks whose parent task is NOT itself starred; shown as their
  // own flat rows under the Starred quick filter (the parent row is hidden).
  // Derived from preStarFilteredTasks so it honors the same active filters.
  const starredSubTaskItems = useMemo(() => {
    return allSubTasks
      .filter(st => st.isStarred)
      .map(st => ({ subTask: st, parentTask: preStarFilteredTasks.find(t => t.id === st.taskId) }))
      .filter((x): x is { subTask: SubTask; parentTask: Task } => !!x.parentTask && x.parentTask.isTopThree !== true);
  }, [allSubTasks, preStarFilteredTasks]);

  // Under the Starred quick filter, each shown (starred) task only displays
  // its starred sub-tasks; otherwise all sub-tasks are shown.
  const displaySubTasksByTaskId = useMemo(() => {
    if (!filters.showTopThreeOnly) return subTasksByTaskId;
    const filtered = new Map<string, SubTask[]>();
    subTasksByTaskId.forEach((subs, taskId) => {
      filtered.set(taskId, subs.filter(st => st.isStarred));
    });
    return filtered;
  }, [subTasksByTaskId, filters.showTopThreeOnly]);

  const filteredAndSortedTasks = useMemo(() => {
    let filtered = preStarFilteredTasks;

    if (filters.showTopThreeOnly) {
      // Only tasks starred themselves; starred sub-tasks of unstarred parents
      // are surfaced as their own flat rows (starredSubTaskItems) instead.
      filtered = filtered.filter(t => t.isTopThree === true);
    }

    // showOnlyLatestRecurring HOTFIX: forced to false to prevent production tasks from disappearing
    const showOnlyLatestRecurring = false;
    if (showOnlyLatestRecurring) {
      const groupedByParent = new Map<string, Task[]>();
      const nonRecurringTasks: Task[] = [];
      filtered.forEach(task => {
        if (task.recurrenceParentId) {
          if (!groupedByParent.has(task.recurrenceParentId)) groupedByParent.set(task.recurrenceParentId, []);
          groupedByParent.get(task.recurrenceParentId)!.push(task);
        } else {
          nonRecurringTasks.push(task);
        }
      });
      const latestRecurringTasks: Task[] = [];
      const now = new Date();
      groupedByParent.forEach((groupTasks) => {
        const sorted = groupTasks.sort((a, b) => {
          if (!a.dueDate && !b.dueDate) return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          const aDate = new Date(a.dueDate);
          const bDate = new Date(b.dueDate);
          if (aDate >= now && bDate >= now) return aDate.getTime() - bDate.getTime();
          if (aDate < now && bDate < now) return bDate.getTime() - aDate.getTime();
          if (aDate >= now) return -1;
          return 1;
        });
        latestRecurringTasks.push(sorted[0]);
      });
      filtered = [...nonRecurringTasks, ...latestRecurringTasks];
    }

    const compareByField = (taskA: Task, taskB: Task, sortBy: string | undefined, sortOrder: string | undefined): number => {
      if (!sortBy || sortBy === "none") return 0;
      let comparison = 0;
      if (sortBy === "displayOrder") {
        comparison = taskA.displayOrder - taskB.displayOrder;
      } else if (sortBy === "dueDate") {
        if (!taskA.dueDate) return 1;
        if (!taskB.dueDate) return -1;
        comparison = new Date(taskA.dueDate).getTime() - new Date(taskB.dueDate).getTime();
      } else if (sortBy === "createdAt") {
        comparison = new Date(taskA.createdAt).getTime() - new Date(taskB.createdAt).getTime();
      } else if (sortBy === "priority") {
        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
        comparison = (priorityOrder[taskA.priority as keyof typeof priorityOrder] || 0) -
                     (priorityOrder[taskB.priority as keyof typeof priorityOrder] || 0);
      } else if (sortBy === "assignedToOther") {
        const aName = taskA.assignedTo === "Others" ? (taskA.assignedToOther || "") : taskA.assignedTo;
        const bName = taskB.assignedTo === "Others" ? (taskB.assignedToOther || "") : taskB.assignedTo;
        comparison = aName.localeCompare(bName);
      }
      return sortOrder === "desc" ? -comparison : comparison;
    };

    return [...filtered].sort((a, b) => {
      const l1 = compareByField(a, b, filters.sortBy1, filters.sortOrder1);
      if (l1 !== 0) return l1;
      const l2 = compareByField(a, b, filters.sortBy2, filters.sortOrder2);
      if (l2 !== 0) return l2;
      return compareByField(a, b, filters.sortBy3, filters.sortOrder3);
    });
  }, [preStarFilteredTasks, subTasksByTaskId, filters.showTopThreeOnly, filters.showOnlyLatestRecurring, filters.sortBy1, filters.sortOrder1, filters.sortBy2, filters.sortOrder2, filters.sortBy3, filters.sortOrder3, filters.groupBy1, filters.groupBy2, filters.groupBy3]);

  type GroupedTasks = { groupName: string; tasks: Task[]; subGroups?: GroupedTasks[] };

  const groupedTasks = useMemo(() => {
    const hasGrouping = filters.groupBy1 !== "none" || filters.groupBy2 !== "none" || filters.groupBy3 !== "none";
    if (!hasGrouping) return null;

    const buildHierarchy = (taskList: Task[], levels: string[], currentLevel = 0): GroupedTasks[] => {
      if (currentLevel >= levels.length || levels[currentLevel] === "none") return [];
      const currentGroupField = levels[currentLevel];
      const groups = new Map<string, Task[]>();
      taskList.forEach(task => {
        const key = String(getGroupValue(task, currentGroupField) ?? "Unknown");
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(task);
      });
      return Array.from(groups.keys()).sort().map(groupName => {
        const groupTasks = groups.get(groupName)!;
        const hasNextLevel = currentLevel + 1 < levels.length && levels[currentLevel + 1] !== "none";
        return hasNextLevel
          ? { groupName, tasks: [], subGroups: buildHierarchy(groupTasks, levels, currentLevel + 1) }
          : { groupName, tasks: groupTasks, subGroups: [] };
      });
    };

    const levels = [filters.groupBy1, filters.groupBy2, filters.groupBy3]
      .filter((l): l is string => l !== "none" && l !== undefined);
    return buildHierarchy(filteredAndSortedTasks, levels);
  }, [filteredAndSortedTasks, filters.groupBy1, filters.groupBy2, filters.groupBy3]);

  const toggleTaskSelection = (taskId: string) => {
    setSelectedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = filteredAndSortedTasks.map(t => t.id);
    const allSelected = visibleIds.every(id => selectedTasks.has(id));
    setSelectedTasks(allSelected ? new Set() : new Set(visibleIds));
  };

  const areAllTasksSelected = filteredAndSortedTasks.length > 0 &&
    filteredAndSortedTasks.every(t => selectedTasks.has(t.id));

  const onShareTask = (task: Task) => { setSharingTask(task); setShareDialogOpen(true); };
  const onScheduleTask = (task: Task) => { setSchedulingTask(task); };

  const baseFilteredTasks = useMemo(() => {
    let filtered = combinedTasks;
    if (filters.completionFilter === "active") filtered = filtered.filter(t => !t.completed);
    else if (filters.completionFilter === "completed") filtered = filtered.filter(t => t.completed);
    if (filters.categoryFilter && filters.categoryFilter.length > 0)
      filtered = filtered.filter(t => t.category && filters.categoryFilter!.includes(t.category));
    if (filters.assignedToFilter === "me") filtered = filtered.filter(t => t.assignedTo === "Me" && !t.isShared);
    else if (filters.assignedToFilter === "others") {
      filtered = filtered.filter(t => t.assignedTo === "Others");
      if (filters.selectedAssigneeNames && filters.selectedAssigneeNames.length > 0)
        filtered = filtered.filter(t => t.assignedToOther && filters.selectedAssigneeNames!.includes(t.assignedToOther));
    } else if (filters.assignedToFilter === "sharedWithMe") filtered = filtered.filter(t => t.isShared === true);
    if (filters.searchQuery)
      filtered = filtered.filter(t =>
        (t.title ?? "").toLowerCase().includes(filters.searchQuery!.toLowerCase()) ||
        t.description?.toLowerCase().includes(filters.searchQuery!.toLowerCase())
      );
    if (filters.dateRangeStart || filters.dateRangeEnd) {
      filtered = filtered.filter(t => {
        if (!t.dueDate) return false;
        const taskDueDate = typeof t.dueDate === "string" ? parseISO(t.dueDate) : new Date(t.dueDate);
        const startDate = filters.dateRangeStart ? startOfDay(new Date(filters.dateRangeStart + "T00:00:00")) : null;
        const endDate = filters.dateRangeEnd ? endOfDay(new Date(filters.dateRangeEnd + "T00:00:00")) : null;
        if (startDate && endDate) return taskDueDate >= startDate && taskDueDate <= endDate;
        if (startDate) return taskDueDate >= startDate;
        if (endDate) return taskDueDate <= endDate;
        return true;
      });
    }
    if (filters.showTopThreeOnly) filtered = filtered.filter(t => t.isTopThree === true);
    return filtered;
  }, [combinedTasks, filters.completionFilter, filters.categoryFilter, filters.assignedToFilter, filters.selectedAssigneeNames, filters.searchQuery, filters.dateRangeStart, filters.dateRangeEnd, filters.showTopThreeOnly, subTasksByTaskId]);

  const taskStats = {
    total: baseFilteredTasks.length,
    standalone: baseFilteredTasks.filter(t => !t.goalId && !t.milestoneId && !t.journalEntryId).length,
    linkedToGoals: baseFilteredTasks.filter(t => t.goalId).length,
    linkedToMilestones: baseFilteredTasks.filter(t => t.milestoneId).length,
    linkedToJournal: baseFilteredTasks.filter(t => t.journalEntryId).length,
    // Use baseFilteredTasks so Active/Completed/Overdue are consistent with Total
    // and respect the same category/search/date filters the list uses.
    active: baseFilteredTasks.filter(t => !t.completed).length,
    completed: baseFilteredTasks.filter(t => t.completed).length,
    overdue: baseFilteredTasks.filter(t => !t.completed && t.dueDate && isPast(new Date(t.dueDate))).length,
  };

  const assignedToOthersTasks = useMemo(() => tasks.filter(t => t.assignedTo === "Others"), [tasks]);
  const assignedToOthersLoading = tasksLoading;

  const assignedToMeStats = useMemo(() => ({
    total: assignedToMeTasks.length,
    active: assignedToMeTasks.filter((t: any) => !t.completed).length,
    completed: assignedToMeTasks.filter((t: any) => t.completed).length,
    overdue: assignedToMeTasks.filter((t: any) => !t.completed && t.dueDate && isPast(new Date(t.dueDate))).length,
  }), [assignedToMeTasks]);

  const teamMembers = useMemo((): TeamMember[] => {
    const members: TeamMember[] = [];
    bulkFriends.forEach(f => members.push(f.friend));
    bulkAssistants.forEach(a => members.push(a.user));
    const seen = new Set<string>();
    return members.filter(m => { if (seen.has(m.id)) return false; seen.add(m.id); return true; });
  }, [bulkFriends, bulkAssistants]);

  const toggleTaskMutation = useMutation({
    mutationFn: async ({ id, completed }: { id: string; completed: boolean }) => {
      const response = await apiRequest("PATCH", `/api/tasks/${id}`, { completed });
      return response.json();
    },
    onMutate: async ({ id, completed }) => {
      const isCurrentSession = capturePersonalCacheBoundary();
      await queryClient.cancelQueries({ queryKey: ["/api/tasks"] });
      if (!isCurrentSession()) return { isCurrentSession };
      const previousTasks = queryClient.getQueryData(["/api/tasks"]);
      queryClient.setQueryData(["/api/tasks"], (old: Task[] | undefined) =>
        old ? old.map(task => task.id === id ? { ...task, completed } : task) : old
      );
      return { previousTasks, isCurrentSession };
    },
    onSuccess: (_, variables, context) => {
      if (context && !context.isCurrentSession()) return;
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks/top-three"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      if (variables.completed) {
        const allTasks = queryClient.getQueryData<Task[]>(["/api/tasks"]);
        const taskTitle = allTasks?.find(t => t.id === variables.id)?.title ?? "Task";
        toast({
          title: "Task completed",
          description: `"${taskTitle.length > 40 ? taskTitle.slice(0, 40) + "…" : taskTitle}"`,
          duration: 15000,
          action: (
            <ToastAction altText="Undo" onClick={() => {
              if (context?.isCurrentSession()) {
                toggleTaskMutation.mutate({ id: variables.id, completed: false });
              }
            }}>
              Undo
            </ToastAction>
          ),
        });
      }
    },
    onError: (error: any, _variables: any, context: any) => {
      if (context && !context.isCurrentSession()) return;
      if (context?.previousTasks) queryClient.setQueryData(["/api/tasks"], context.previousTasks);
      const message = String(error?.message ?? "");
      const isPermissionError = isEditPermissionError(error);
      toast({
        title: isPermissionError ? "View Only" : "Couldn't update task",
        description: isPermissionError
          ? "You can't edit this shared task — you only have view access."
          : message || "Your change was not saved.",
        variant: "destructive",
      });
    },
  });

  const quickProgressMutation = useMutation({
    mutationFn: async ({ taskId, notes }: { taskId: string; notes: string }) => {
      const response = await apiRequest("POST", `/api/tasks/${taskId}/progress`, { notes });
      return response.json();
    },
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", taskId, "progress"] });
      queryClient.invalidateQueries({ queryKey: ["/api/task-progress"] });
      toast({ title: "Success", description: "Progress entry added successfully" });
      setIsQuickProgressOpen(false);
      setQuickProgressTaskId("");
      setQuickProgressNote("");
      setQuickProgressPopoverId(null);
      setQuickProgressPopoverNote("");
    },
    onError: (error: any) => {
      const isPermissionError = isEditPermissionError(error);
      toast({
        title: isPermissionError ? "View Only" : "Error",
        description: isPermissionError
          ? "You can't edit this shared task — you only have view access."
          : "Failed to add progress entry",
        variant: "destructive",
      });
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/tasks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      setDeletingTaskId(null);
    },
    onError: (error: Error) => {
      // Surface server-side rejections (e.g. no edit permission on a shared
      // task) and refetch so the optimistically-removed task reappears.
      const permissionError = isEditPermissionError(error);
      toast({
        title: permissionError ? "View Only" : "Couldn't delete task",
        description: permissionError
          ? "You can't delete this shared task — you only have view access."
          : error?.message || "The task was not deleted.",
        variant: "destructive",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
    },
  });

  const handleConfirmDelete = (taskId: string) => {
    const isCurrentSession = capturePersonalCacheBoundary();
    if (pendingDeleteRef.current) {
      clearTimeout(pendingDeleteRef.current.timeoutId);
      if (pendingDeleteRef.current.taskId !== taskId) deleteTaskMutation.mutate(pendingDeleteRef.current.taskId);
      pendingDeleteRef.current = null;
    }
    const allTasks = queryClient.getQueryData<Task[]>(["/api/tasks"]);
    const taskSnapshot = allTasks?.find(t => t.id === taskId);
    const taskTitle = taskSnapshot?.title ?? "Task";
    queryClient.setQueryData<Task[]>(["/api/tasks"], old => old ? old.filter(t => t.id !== taskId) : old);
    setDeletingTaskId(null);
    const timeoutId = setTimeout(() => {
      pendingDeleteRef.current = null;
      if (isCurrentSession()) deleteTaskMutation.mutate(taskId);
    }, 15000);
    pendingDeleteRef.current = { taskId, timeoutId };
    const { dismiss } = toast({
      title: "Task deleted",
      description: `"${taskTitle.length > 40 ? taskTitle.slice(0, 40) + "…" : taskTitle}"`,
      duration: 15000,
      action: (
        <ToastAction altText="Undo" onClick={() => {
          clearTimeout(timeoutId);
          pendingDeleteRef.current = null;
          if (taskSnapshot && isCurrentSession()) {
            queryClient.setQueryData<Task[]>(["/api/tasks"], old => old ? [...old, taskSnapshot] : [taskSnapshot]);
            queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
          }
          dismiss();
        }}>
          Undo
        </ToastAction>
      ),
    });
  };

  const toggleTopThreeMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const response = await apiRequest("POST", `/api/tasks/${taskId}/toggle-top-three`);
      let data: any;
      try { data = await response.json(); } catch { data = { success: false, message: `HTTP ${response.status}` }; }
      if (!response.ok && response.status === 400) return data;
      if (!response.ok) throw new Error(data.error || data.message || `Request failed with status ${response.status}`);
      return data;
    },
    onSuccess: async (result: { success: boolean; message?: string; task?: any }) => {
      if (result.success) {
        await queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
        await queryClient.refetchQueries({ queryKey: ["/api/tasks/top-three"] });
      }
    },
    onError: (error: any) => {
      const isPermissionError = isEditPermissionError(error);
      toast({
        title: isPermissionError
          ? "View Only"
          : "Error",
        description: isPermissionError
          ? "You can't edit this shared task — you only have view access."
          : "Failed to update the task. Please try again.",
        variant: "destructive",
      });
    },
  });

  const reorderTasksMutation = useMutation({
    mutationFn: async (taskIds: string[]) => { await apiRequest("PATCH", "/api/tasks/reorder", { taskIds }); },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/tasks"] }); },
    onError: () => {},
  });

  const bulkUpdateTasksMutation = useMutation({
    mutationFn: async ({ taskIds, updates }: { taskIds: string[]; updates: any }) => {
      const response = await apiRequest("PATCH", "/api/tasks/bulk-update", { taskIds, updates });
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
      await queryClient.refetchQueries({ queryKey: ["/api/stats/dashboard"] });
      toast({ title: "Changes saved", description: "Bulk updates applied successfully." });
    },
    onError: (error: unknown) => {
      // Always re-sync the task list so any partial server-side change (or an
      // optimistic removal) is reflected in the UI even when the batch fails.
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      const isSelectionConflict =
        (error as { status?: number; code?: string })?.status === 409 &&
        (error as { status?: number; code?: string })?.code === "TASK_SELECTION_CHANGED";
      if (isSelectionConflict) {
        // The server rolled back the whole assignment transaction. Clear the
        // stale selection and close the dialog so a retry cannot resend the
        // task ID that disappeared during the request.
        setSelectedTasks(new Set());
        setPendingBulkUpdates({});
        setIsBulkActionsOpen(false);
        const refreshTaskLists = () => Promise.all([
          queryClient.refetchQueries({ queryKey: ["/api/tasks"] }),
          queryClient.refetchQueries({ queryKey: ["/api/shared-tasks"] }),
        ]);
        void refreshTaskLists();
        toast({
          title: "Selection changed",
          description: "A selected task was removed. No tasks were assigned. Your list was refreshed—select the remaining tasks and try again.",
          variant: "destructive",
          action: (
            <ToastAction altText="Refresh tasks" onClick={() => { void refreshTaskLists(); }}>
              Refresh
            </ToastAction>
          ),
        });
        return;
      }
      const permissionError = isEditPermissionError(error);
      toast({
        title: permissionError ? "View Only" : "Error",
        description: permissionError
          ? "You can't edit this shared task — you only have view access."
          : "Failed to apply bulk updates. Please try again.",
        variant: "destructive",
      });
    },
  });

  const bulkDeleteTasksMutation = useMutation({
    mutationFn: async (taskIds: string[]) => {
      const response = await apiRequest("POST", "/api/tasks/bulk-delete", { taskIds });
      return response.json() as Promise<{ deletedIds: string[] }>;
    },
    onSuccess: ({ deletedIds }) => {
      setSelectedTasks(previous => {
        const next = new Set(previous);
        deletedIds.forEach(id => next.delete(id));
        return next;
      });
      setIsBulkActionsOpen(false);
      setPendingBulkUpdates({});
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/shared-tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/goals"] }),
      ]);
      toast({
        title: "Tasks deleted",
        description: `${deletedIds.length} task${deletedIds.length === 1 ? "" : "s"} deleted.`,
      });
    },
    onError: (error: Error) => {
      // A lost response can happen after the server commits. Keep the
      // selection, but do not incorrectly promise that nothing was deleted.
      toast({ title: "Couldn't delete tasks", description: "Your selection is kept. Refresh the list to confirm the current state before retrying. " + error.message, variant: "destructive" });
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/shared-tasks"] }),
      ]);
    },
  });

  const deleteSelectedTasks = (taskIds: string[]) => {
    // No partial delete if a shared, assigned, or stale selection is present.
    const ownedIds = new Set(tasks.filter(task => task.userId === user?.id).map(task => task.id));
    if (!taskIds.length || taskIds.some(id => !ownedIds.has(id))) {
      toast({
        title: "Selection cannot be deleted",
        description: "Only your own tasks can be deleted. Refresh your list and select only tasks you own.",
        variant: "destructive",
      });
      return;
    }
    bulkDeleteTasksMutation.mutate(taskIds);
  };

  const getLinkedItemName = (task: Task | null | undefined) => {
    if (!task) return null;
    if (task.goalId) {
      const goal = goals.find(g => g.id === task.goalId);
      return goal ? { type: "Goal", name: goal.title } : null;
    }
    if (task.journalEntryId) {
      const entry = journalEntries.find(e => e.id === task.journalEntryId);
      return entry ? { type: "Journal", name: entry.title } : null;
    }
    if (task.milestoneId) return { type: "Milestone", name: "Milestone" };
    return null;
  };

  const isOverdue = (task: Task | null | undefined): boolean => {
    if (!task) return false;
    return !task.completed && task.dueDate != null && isPast(new Date(task.dueDate));
  };

  const handleTaskMove = (taskId: string, columnId: string | null) => { moveTaskMutation.mutate({ taskId, columnId }); };

  const handleColumnCreate = (data: { title: string; color: string }) => {
    if (!user || !tenant) return;
    const maxOrder = Math.max(...columns.map(c => c.displayOrder), -1);
    createColumnMutation.mutate({ ...data, tenantId: tenant.id, userId: user.id, displayOrder: maxOrder + 1 });
  };

  const handleColumnUpdate = (id: string, updates: { title?: string; color?: string }) => {
    updateColumnMutation.mutate({ id, updates });
  };

  const handleColumnDelete = (id: string) => { setDeletingColumnId(id); };
  const handleColumnReorder = (columnIds: string[]) => { reorderColumnsMutation.mutate(columnIds); };

  const handleColumnSave = (data: { title: string; color: string }) => {
    if (editingColumn) handleColumnUpdate(editingColumn.id, data); else handleColumnCreate(data);
  };

  const handleDragEnd = (event: DragEndEvent, tasksInGroup: Task[]) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tasksInGroup.findIndex(t => t.id === active.id);
    const newIndex = tasksInGroup.findIndex(t => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reorderedGroup = arrayMove(tasksInGroup, oldIndex, newIndex);
    const groupTaskIds = new Set(reorderedGroup.map(t => t.id));
    const allTasksSorted = [...tasks].sort((a, b) => a.displayOrder - b.displayOrder);
    const result: Task[] = [];
    let groupIdx = 0;
    for (const task of allTasksSorted) {
      if (groupTaskIds.has(task.id)) { result.push(reorderedGroup[groupIdx]); groupIdx++; }
      else result.push(task);
    }
    reorderTasksMutation.mutate(result.map(t => t.id));
  };

  const toggleGroupCollapsed = (groupPath: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupPath)) next.delete(groupPath); else next.add(groupPath);
      return next;
    });
  };

  void perTaskOverridesInitialized;

  return {
    tasksLoading,
    tasks,
    tasksError,
    refetchTasks,
    goals,
    journalEntries,
    categories,
    columns,
    columnsLoading,
    assignedToMeLoading,
    assignedToMeTasks,
    assignedToMeError,
    refetchAssignedToMe,
    assignedToMeStats,
    assignedToOthersLoading,
    assignedToOthersTasks,
    sharedTasksLoading,
    sharedTasks,
    updateSharedTaskMutation,
    teamMembers,
    filters,
    updateFilters,
    isFiltersExpanded,
    setIsFiltersExpanded,
    activeMainTab,
    setActiveMainTab,
    filteredAndSortedTasks,
    groupedFilteredTasks: groupedTasks,
    taskStats,
    subTasksByTaskId,
    displaySubTasksByTaskId,
    starredSubTaskItems,
    areAllTasksSelected,
    isProgressLogOpen,
    setIsProgressLogOpen,
    progressLogCategoryFilter,
    setProgressLogCategoryFilter,
    progressLogUserFilter,
    setProgressLogUserFilter,
    progressLogStartDate,
    setProgressLogStartDate,
    progressLogEndDate,
    setProgressLogEndDate,
    progressLogLoading,
    progressLogEntries,
    progressLogAssignees,
    isQuickProgressOpen,
    setIsQuickProgressOpen,
    quickProgressTaskId,
    setQuickProgressTaskId,
    quickProgressNote,
    setQuickProgressNote,
    quickProgressPopoverId,
    setQuickProgressPopoverId,
    quickProgressPopoverNote,
    setQuickProgressPopoverNote,
    selectedTasks,
    setSelectedTasks,
    isBulkActionsOpen,
    setIsBulkActionsOpen,
    pendingBulkUpdates,
    setPendingBulkUpdates,
    isCreateModalOpen,
    setIsCreateModalOpen,
    createTaskColumnId,
    setCreateTaskColumnId,
    editingTask,
    setEditingTask,
    viewingTask,
    setViewingTask,
    deletingTaskId,
    setDeletingTaskId,
    createNoteForTask,
    setCreateNoteForTask,
    isColumnDialogOpen,
    setIsColumnDialogOpen,
    editingColumn,
    setEditingColumn,
    deletingColumnId,
    setDeletingColumnId,
    shareDialogOpen,
    setShareDialogOpen,
    sharingTask,
    setSharingTask,
    schedulingTask,
    setSchedulingTask,
    sensors,
    collapsedGroups,
    toggleTaskMutation,
    toggleTopThreeMutation,
    quickProgressMutation,
    deleteColumnMutation,
    bulkUpdateTasksMutation,
    bulkDeleteTasksMutation,
    deleteSelectedTasks,
    toggleTaskSelection,
    toggleSelectAll,
    onShareTask,
    getLinkedItemName,
    isOverdue,
    getSubTaskProgress,
    getHideCompleted,
    onToggleHideCompleted: togglePerTaskHideCompleted,
    toggleGroupCollapsed,
    handleDragEnd,
    handleTaskMove,
    handleColumnDelete,
    handleColumnReorder,
    handleColumnSave,
    handleConfirmDelete,
    onScheduleTask,
    onSubtaskToggle,
    onSubtaskStar,
    onSubtaskDateChange,
    onSubtaskTextChange,
    onSubtaskReorder,
    onSubtaskDelete,
    setPerTaskCompletedOverrides,
    toast,
    user,
    tenant,
  };
}
